import React, { FormEvent, useState, useCallback, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { BiSolidShare } from "react-icons/bi";

import Container from "./Container";
import DateRangeInput from "./DateRangeInput";
import ClassDropdown from "./ClassDropdown";
import StudentDropdown from "./StudentDropdown";
import HashtagInput from "./HashtagInput";
import IsFavoriteCheckbox from "./IsFavoriteCheckbox";
import PrimaryActionButton from "./PrimaryActionButton";
import SearchIcon from "assets/icons/SearchIcon";
import { searchHashTag } from "services/pictureServices";
import { PictureStudentOption } from "config/constants";

interface SearchParams {
  startDate?: string;
  endDate?: string;
  classId?: number;
  studentId?: number | PictureStudentOption | null;
  hashtagId?: number;
  isFavorite?: boolean;
}

interface PictureSearchInputProps {
  onSearchChange: (searchParams: SearchParams) => void;
  initialValues?: SearchParams;
  isLoading?: boolean; // Added to show loading state from parent
}

const TODAY = new Date();
TODAY.setHours(23, 59, 59, 999);

const normalizeHashtagValue = (value: string) =>
  value.replace(/^#/, "").trim().normalize("NFKC");

// Helper function to format date as YYYY-MM-DD
const formatDateToYMD = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

// Helper function to parse YYYY-MM-DD string to Date
const parseDateFromYMD = (dateStr: string): Date => {
  const [year, month, day] = dateStr.split("-").map(Number);
  return new Date(year, month - 1, day);
};

const PictureSearchInput: React.FC<PictureSearchInputProps> = ({
  onSearchChange,
  initialValues = {},
  isLoading = false,
}) => {
  const { t } = useTranslation();

  // Local state for search parameters - now storing dates as YYYY-MM-DD strings
  const [startDate, setStartDate] = useState<string>(
    initialValues.startDate || formatDateToYMD(new Date()),
  );
  const [endDate, setEndDate] = useState<string>(
    initialValues.endDate || formatDateToYMD(new Date()),
  );
  const [classId, setClassId] = useState<number | null>(
    initialValues.classId || null,
  );
  const [studentId, setStudentId] = useState<
    number | PictureStudentOption | null
  >(initialValues.studentId || null);
  const [hashtagId, setHashtagId] = useState<number | null>(
    initialValues.hashtagId || null,
  );
  const [isFavorite, setIsFavorite] = useState<boolean>(
    initialValues.isFavorite || false,
  );
  const [hashTagInputValue, setHashTagInputValue] = useState("");

  // Sync internal state with initialValues when they change
  useEffect(() => {
    if (initialValues.startDate !== undefined) {
      setStartDate(initialValues.startDate);
    }
    if (initialValues.endDate !== undefined) {
      setEndDate(initialValues.endDate);
    }
    if (initialValues.classId !== undefined) {
      setClassId(initialValues.classId);
    }
    if (initialValues.studentId !== undefined) {
      setStudentId(initialValues.studentId);
    }
    if (initialValues.hashtagId !== undefined) {
      setHashtagId(initialValues.hashtagId);
    }
    if (initialValues.isFavorite !== undefined) {
      setIsFavorite(initialValues.isFavorite);
    }
  }, [initialValues]);

  // Memoize the onSearchChange callback to prevent unnecessary calls
  const memoizedOnSearchChange = useCallback(onSearchChange, [onSearchChange]);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();

    // Handle hashtag search if needed
    let finalHashtagId = hashtagId;
    if (hashTagInputValue.length > 0 && !hashtagId) {
      const hashtags = await searchHashTag(hashTagInputValue);
      const normalizedInput = normalizeHashtagValue(hashTagInputValue);
      const foundHashtag = hashtags.find(
        (tag) => normalizeHashtagValue(tag.name) === normalizedInput,
      );
      finalHashtagId = foundHashtag ? foundHashtag.id : null;
      setHashtagId(finalHashtagId);
    }

    const finalSearchParams: SearchParams = {
      startDate,
      endDate,
      classId: classId || undefined,
      studentId: studentId || undefined,
      isFavorite,
    };

    // Only add hashtagId if it has a truthy value and there's input text
    if (finalHashtagId && hashTagInputValue.length > 0) {
      finalSearchParams.hashtagId = finalHashtagId;
    }

    // Update parent component with search parameters - let useEffect handle the search
    memoizedOnSearchChange(finalSearchParams);
  };

  const handleDateChange = (dates: Date[]) => {
    setStartDate(formatDateToYMD(dates[0]));
    setEndDate(formatDateToYMD(dates[1]));
  };

  const handleClassChange = (id: number | null) => {
    setClassId(id);
    setStudentId(null); // Reset student when class changes
  };

  const handleStudentChange = (id: number | string | null) => {
    if (id === PictureStudentOption.all || id === PictureStudentOption.class) {
      setStudentId(id);
    } else {
      setStudentId(id ? Number(id) : null);
    }
  };

  const handleHashtagChange = (value: number | null) => {
    setHashtagId(value);
  };

  const handleFavoriteChange = (status: boolean) => {
    setIsFavorite(status);
  };

  const handleClear = () => {
    const today = formatDateToYMD(new Date());
    setStartDate(today);
    setEndDate(today);
    setClassId(null);
    setStudentId(null);
    setHashtagId(null);
    setIsFavorite(false);
    setHashTagInputValue("");

    // Also update the search parameters with the cleared values
    memoizedOnSearchChange({
      startDate: today,
      endDate: today,
      classId: undefined,
      studentId: undefined,
      isFavorite: false,
      // Explicitly exclude hashtagId
    });
  };

  return (
    <Container>
      <form
        className="grid grid-cols-1 gap-2 lg:grid-cols-[minmax(270px,1.8fr)_minmax(120px,0.8fr)_minmax(148px,1fr)_minmax(122px,0.85fr)_minmax(136px,0.85fr)_minmax(120px,0.75fr)] lg:items-start"
        onSubmit={handleSubmit}
      >
        <div className="w-full flex">
          <DateRangeInput
            name={""}
            currentStartDate={parseDateFromYMD(startDate)}
            currentEndDate={parseDateFromYMD(endDate)}
            maxDate={TODAY}
            onChange={handleDateChange}
          />
        </div>
        <div className="w-full flex">
          <ClassDropdown
            currentClassId={classId?.toString() || ""}
            onChange={handleClassChange}
          />
        </div>
        <div className="w-full flex">
          <StudentDropdown
            currentClassId={classId || 0}
            currentStudentId={studentId ? studentId.toString() : ""}
            onChange={handleStudentChange}
          />
        </div>
        <div className="w-full flex">
          <HashtagInput
            inputValue={hashTagInputValue}
            onInputChange={(value) => setHashTagInputValue(value)}
            value={hashtagId}
            onChange={handleHashtagChange}
          />
        </div>
        <div className="w-full flex items-center">
          <IsFavoriteCheckbox
            isFavorite={isFavorite}
            onChange={handleFavoriteChange}
          />
        </div>
        <div className="w-full flex flex-col justify-center space-y-2">
          <PrimaryActionButton
            isButton={false}
            label={
              <div className="w-fit m-auto flex items-center space-x-1">
                <SearchIcon width={24} height={24} color={"white"} />
                <p>{t("_published_picture._search")}</p>
              </div>
            }
            disabled={!classId || !studentId}
            loading={isLoading}
          />
          <button
            type="button"
            className="px-3 py-2 bg-slate-600 hover:bg-slate-700 text-white whitespace-nowrap rounded flex items-center justify-center space-x-1"
            onClick={handleClear}
          >
            <BiSolidShare size={14} className="shrink-0 text-white" />
            <span className="text-[9px] leading-none">
              {t("_published_picture._clear")}
            </span>
          </button>
        </div>
      </form>
    </Container>
  );
};

export default PictureSearchInput;
